// Frontend/src/lib/issueStyles.ts
//
// One visual vocabulary for issues, shared by the admin console and the
// resident portal so the same issue never looks like two different things.
//
// The discipline behind the palette — a card has three facts competing for
// attention, and only one of them may be loud:
//
//   status    owns COLOUR   — where it is in its life. Rail, chip, icon tint.
//   priority  owns WEIGHT   — how loud. A dot and a label; only URGENT fills.
//   category  owns the ICON — what kind of problem. Colour stays neutral.
//
// The tones are the dashboard's: a soft tinted ground, a matching hairline
// ring, and dark text of the same hue — the `Stat` tile treatment in
// DashboardPage.tsx. Rounded, quiet, and already familiar from the home page.
//
// Colours are inline CSSProperties rather than Tailwind classes on purpose:
// this file is .ts, and a class string that only ever appears outside a .tsx
// file is one Tailwind's scanner can miss, which fails as a silently
// unstyled chip in production and looks fine in dev.
//
// Every hue is drawn from the app's own tokens in index.css:
//   primary #1F3A93 · accent #27AE60 · destructive #DC2626 · border #E5E7EB

import type { CSSProperties } from "react";
import {
  AlertOctagon,
  ArrowUp,
  Ban,
  Building2,
  Car,
  CheckCircle2,
  Clock,
  Droplets,
  Flame,
  Key,
  Layers,
  Leaf,
  Minus,
  MoveVertical,
  PaintRoller,
  ShieldCheck,
  Timer,
  Trash2,
  Volume2,
  Wifi,
  Wind,
  Wrench,
  Zap,
  type LucideIcon,
} from "lucide-react";

import type { IssuePriority, IssueStatus } from "@/types/issue";

export const BRAND = "#1F3A93";
export const BRAND_ACCENT = "#27AE60";

export type Tone = {
  /** Left rail on a card. The strongest signal in the list. */
  rail: string;
  /** Chip and tile background. */
  bg: string;
  /** Chip text, and the icon tint. */
  fg: string;
  /** Chip hairline, so a pale chip still has an edge on white. */
  ring: string;
};

/* ------------------------------------------------------------------ *
 * Status — owns colour
 * ------------------------------------------------------------------ */
export const STATUS_TONE: Record<IssueStatus, Tone> = {
  // Waiting on someone. Amber reads as "not yet", not as "wrong".
  PENDING: { rail: "#D97706", bg: "#FEF3C7", fg: "#92400E", ring: "#FDE0A3" },
  // Being worked. The brand blue, because this is the healthy middle state.
  IN_PROGRESS: { rail: BRAND, bg: "#E7EBF8", fg: "#1B3480", ring: "#C3CDEC" },
  // Done. The app's accent green.
  RESOLVED: { rail: BRAND_ACCENT, bg: "#DCF5E6", fg: "#12693C", ring: "#B4E4C7" },
  // Closed without action. Deliberately the quietest thing on the page.
  CANCELLED: { rail: "#94A3B8", bg: "#F1F5F9", fg: "#475569", ring: "#E2E8F0" },
};

export const STATUS_ICON: Record<IssueStatus, LucideIcon> = {
  PENDING: Clock,
  IN_PROGRESS: Timer,
  RESOLVED: CheckCircle2,
  CANCELLED: Ban,
};

/* ------------------------------------------------------------------ *
 * Priority — owns weight
 *
 * Only URGENT gets a filled chip. If three levels all shout, the one that
 * matters stops being findable.
 * ------------------------------------------------------------------ */
export const PRIORITY_TONE: Record<IssuePriority, Tone> = {
  URGENT: { rail: "#DC2626", bg: "#FEE2E2", fg: "#991B1B", ring: "#F8B4B4" },
  HIGH: { rail: "#EA580C", bg: "#FFEDD5", fg: "#9A3412", ring: "#FCC89B" },
  MEDIUM: { rail: "#D97706", bg: "#FEF3C7", fg: "#92400E", ring: "#FDE0A3" },
  LOW: { rail: "#94A3B8", bg: "#F1F5F9", fg: "#475569", ring: "#E2E8F0" },
};

export const PRIORITY_ICON: Record<IssuePriority, LucideIcon> = {
  URGENT: AlertOctagon,
  HIGH: Flame,
  MEDIUM: ArrowUp,
  LOW: Minus,
};

/** URGENT is the only priority allowed a filled chip. */
export function priorityIsLoud(priority: IssuePriority): boolean {
  return priority === "URGENT";
}

/* ------------------------------------------------------------------ *
 * Category — owns the icon, stays neutral
 *
 * Categories are rows an admin owns now, not an enum, so this maps the
 * icon NAME the server stores. An unknown name falls back rather than
 * rendering nothing — an admin should never be able to break a list by
 * choosing an icon this build has not heard of.
 * ------------------------------------------------------------------ */
const ICON_BY_NAME: Record<string, LucideIcon> = {
  wrench: Wrench,
  droplets: Droplets,
  zap: Zap,
  wind: Wind,
  elevator: MoveVertical,
  building: Building2,
  volume: Volume2,
  shield: ShieldCheck,
  layers: Layers,
  paint: PaintRoller,
  trash: Trash2,
  key: Key,
  wifi: Wifi,
  car: Car,
  leaf: Leaf,
  flame: Flame,
};

export function categoryIcon(name: string | null | undefined): LucideIcon {
  return ICON_BY_NAME[(name ?? "").toLowerCase()] ?? Layers;
}

/** The icons an admin may pick from, in the order the picker shows them. */
export const CATEGORY_ICON_CHOICES: { name: string; Icon: LucideIcon }[] = [
  "wrench", "droplets", "zap", "wind", "elevator", "building", "volume",
  "shield", "layers", "paint", "trash", "key", "wifi", "car", "leaf", "flame",
].map((name) => ({ name, Icon: ICON_BY_NAME[name] }));

/* ------------------------------------------------------------------ *
 * Ready-made style objects
 * ------------------------------------------------------------------ */
export function chipStyle(tone: Tone): CSSProperties {
  return {
    background: tone.bg,
    color: tone.fg,
    boxShadow: `inset 0 0 0 1px ${tone.ring}`,
  };
}

/** A neutral chip, for facts that must not compete: unit, category, counts. */
export function quietChipStyle(): CSSProperties {
  return {
    background: "#F8FAFC",
    color: "#475569",
    boxShadow: "inset 0 0 0 1px #E5E7EB",
  };
}

export function railStyle(status: IssueStatus): CSSProperties {
  return { background: STATUS_TONE[status].rail };
}

/**
 * The card's left rail, as a rounded left BORDER rather than an absolutely
 * positioned bar.
 *
 * The dashboard's Card does it this way, and the difference is visible: a
 * positioned bar runs square into the card's rounded corner and reads as a
 * hard edge stuck on the side, while a border follows the radius and looks
 * like part of the card.
 */
export function cardRailStyle(status: IssueStatus): CSSProperties {
  return { borderLeftColor: STATUS_TONE[status].rail };
}

/** Tinted circle behind the category icon, so the card has a focal point. */
export function iconWellStyle(status: IssueStatus): CSSProperties {
  const tone = STATUS_TONE[status];
  return { background: tone.bg, color: tone.fg };
}

/** The dashboard's Stat-tile treatment: soft ground, matching ring, dark text. */
export function tileStyle(tone: Tone): CSSProperties {
  return {
    background: tone.bg,
    color: tone.fg,
    boxShadow: `inset 0 0 0 1px ${tone.ring}`,
  };
}

/** A resolved or cancelled issue is finished business; let it recede. */
export function isSettled(status: IssueStatus): boolean {
  return status === "RESOLVED" || status === "CANCELLED";
}
